import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectSubOrganizationsData } from '../../store/organization/organization-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { resetOrganizationsRestState } from '../../store/organization/organization-slice';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import OrganizationRole from '../../models/enums/organization-roles';
import {
  validateOrganizationAddress,
  validateOrganizationCity,
  validateOrganizationName,
  validateOrganizationPhoneNumber,
  validateOrganizationZipCode,
} from '../../services/validation/organization-validation';
import TextInput from '@common/components/Inputs/TextInput';
import {
  createSubOrganization,
  patchOrganizationById,
} from '../../store/organization/organization-actions';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import CountryCode from '../../models/enums/country-codes';
import SelectInput from '../forms/SelectInput';
import Organization from '../../models/organization/organization';

const OrganizationsForm: React.FC<{
  organization: Organization | null;
  parentId: string | null;
  open: boolean;
  role: OrganizationRole;
  initialization?: boolean;
  onClose: () => void;
}> = ({ open, organization, parentId, role, initialization, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const organizationData = useAppSelector((state) => selectSubOrganizationsData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: organizationData.rest.postError,
      patchError: organizationData.rest.patchError,
      resetPostHandler: () =>
        dispatch(
          resetOrganizationsRestState({ postStatus: HttpFetchStatus.Idle, postError: null }),
        ),
      resetPatchHandler: () =>
        dispatch(
          resetOrganizationsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }),
        ),
    });

  useEffect(() => {
    if (
      (organizationData.rest.postStatus === HttpFetchStatus.Success ||
        organizationData.rest.patchStatus === HttpFetchStatus.Success) &&
      open
    ) {
      updateResetToggle();
      onClose();
    }
  }, [
    onClose,
    open,
    organizationData.rest.patchStatus,
    organizationData.rest.postStatus,
    updateResetToggle,
  ]);

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    organization?.name ?? '',
    (newName) => validateOrganizationName(newName, role),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const {
    value: phoneNumber,
    validationResult: phoneNumberValidationResult,
    valueChangeHandler: phoneNumberChangeHandler,
  } = useValidatedInputEffect(
    organization?.phoneNumber ?? '',
    (newValue) => validateOrganizationPhoneNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('phoneNumber'),
    removeServerError,
    resetToggle,
  );

  const {
    value: address,
    validationResult: addressValidationResult,
    valueChangeHandler: addressChangeHandler,
  } = useValidatedInputEffect(
    organization?.address ?? '',
    (newValue) => validateOrganizationAddress(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('address'),
    removeServerError,
    resetToggle,
  );

  const {
    value: city,
    validationResult: cityValidationResult,
    valueChangeHandler: cityChangeHandler,
  } = useValidatedInputEffect(
    organization?.city ?? '',
    (newValue) => validateOrganizationCity(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('city'),
    removeServerError,
    resetToggle,
  );

  const {
    value: zipCode,
    validationResult: zipCodeValidationResult,
    valueChangeHandler: zipCodeChangeHandler,
  } = useValidatedInputEffect(
    organization?.zipCode ?? '',
    (newValue) => validateOrganizationZipCode(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('zipCode'),
    removeServerError,
    resetToggle,
  );

  const {
    value: erpId,
    validationResult: erpIdValidationResult,
    valueChangeHandler: erpIdChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => validateOrganizationZipCode(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('erpId'),
    removeServerError,
    resetToggle,
  );

  const {
    value: country,
    validationResult: countryValidationResult,
    valueChangeHandler: countryChangeHandler,
  } = useValidatedSelectEffect(
    organization?.country ?? CountryCode.SWITZERLAND,
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('country'),
    removeServerError,
    resetToggle,
  );

  const closeHandler = () => {
    dispatch(
      resetOrganizationsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
      }),
    );
    updateResetToggle();
    onClose();
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (organization?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organization.id,
          data: {
            name,
            phoneNumber,
            address,
            city,
            country: country as CountryCode,
            zipCode,
          },
        }),
      );
    } else {
      dispatch(
        createSubOrganization({
          name,
          role,
          parentId: parentId ?? undefined,
          phoneNumber,
          address,
          city,
          country: country as CountryCode,
          zipCode,
        }),
      );
    }
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={formSubmitHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {organization?.id != null ? name : t('title-create-' + role)}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={2}>
              {initialization === true && (
                <CustomAlert severity="info" header={t('info-initialize-organization-data')} />
              )}
              {organizationData.rest.postError !== null &&
                isHttpError(organizationData.rest.postError) && (
                  <CustomAlert
                    severity="error"
                    header={t(organizationData.rest.postError.message)}
                  />
                )}
              {organizationData.rest.patchError !== null &&
                isHttpError(organizationData.rest.patchError) && (
                  <CustomAlert
                    severity="error"
                    header={t(organizationData.rest.patchError.message)}
                  />
                )}
              <TextInput
                value={name}
                onChange={nameChangeHandler}
                validationResult={nameValidationResult}
                label={t('input-label-organization-name')}
              />
              {role === OrganizationRole.CUSTOMER && (
                <TextInput
                  value={erpId}
                  onChange={erpIdChangeHandler}
                  validationResult={erpIdValidationResult}
                  label={t('input-label-organization-erp-id')}
                />
              )}
              <TextInput
                value={phoneNumber}
                onChange={phoneNumberChangeHandler}
                validationResult={phoneNumberValidationResult}
                label={t('input-label-organization-phone-number')}
              />
              <Stack spacing={2}>
                <TextInput
                  value={address}
                  onChange={addressChangeHandler}
                  validationResult={addressValidationResult}
                  label={t('input-label-organization-address')}
                />
                <TextInput
                  value={city}
                  onChange={cityChangeHandler}
                  validationResult={cityValidationResult}
                  label={t('input-label-organization-city')}
                />
                <Stack direction="row">
                  <SelectInput
                    label={t('input-label-organization-country')}
                    value={country}
                    onChange={countryChangeHandler}
                    validationResult={countryValidationResult}
                  >
                    {Object.values(CountryCode).map((c) => (
                      <MenuItem key={c} value={c}>
                        {t('country-' + c)}
                      </MenuItem>
                    ))}
                  </SelectInput>
                  <TextInput
                    value={zipCode}
                    onChange={zipCodeChangeHandler}
                    validationResult={zipCodeValidationResult}
                    label={t('input-label-organization-zip-code')}
                  />
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              {initialization !== true && (
                <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              )}
              <Button
                variant="contained"
                data-testid="button-add"
                disabled={
                  organizationData.rest.postStatus === HttpFetchStatus.Loading ||
                  organizationData.rest.patchStatus === HttpFetchStatus.Loading
                }
                type="submit"
              >
                {organizationData.rest.postStatus === HttpFetchStatus.Loading ||
                organizationData.rest.patchStatus === HttpFetchStatus.Loading ? (
                  <CircularProgress size={24} />
                ) : organization?.id == null ? (
                  t('button-add-' + role)
                ) : (
                  t('button-save')
                )}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      <CustomSnackbar
        open={organizationData.rest.postStatus === HttpFetchStatus.Success && open}
        title={t('success-creating-' + role)}
        onClose={closeHandler}
      />
      <CustomSnackbar
        open={organizationData.rest.patchStatus === HttpFetchStatus.Success && open}
        title={t('success-patching-' + role)}
        onClose={closeHandler}
      />
    </React.Fragment>
  );
};

export default OrganizationsForm;
